// Admin Recordings — '/admin/recordings'.
//
// Ports the legacy `Recordings` React component
// (www/livevane.com/htdocs/admin/index.html ~3122–3607) and its
// co-located `ScheduleModal` (legacy 2936) into a TanStack file-based
// route under the /admin layout.
//
// Auth gate:
//   NOT re-added in this file. The parent layout at
//   apps/web/src/routes/admin/route.tsx already throws-redirects to
//   /login when `token === null || user?.role !== 'admin'` inside its
//   beforeLoad. Child routes only handle their own data loading.
//
// API endpoints (verified against legacy admin + backing PHP):
//   - GET    /api/recordings/${vaSuffix}                 → {recordings, featured}
//   - POST   /api/recordings/  {action:'clear'}          → clear featured
//   - POST   /api/recordings/  {action:'delete', path}   → delete file
//   - POST   /api/recordings/  {action:'label', path, label} → set/clear label
//   - GET    /api/recordings/schedule/${vaSuffix}        → {schedules}
//   - POST   /api/recordings/schedule/  {action:'create', path, start_at, end_at?, duration_seconds?}
//   - DELETE /api/recordings/schedule/  ?action=cancel|delete&id=
//   - GET    /api/stream/${vaSuffix}                     → stream keys, used only
//     to show the key's NAME in the Owner column (shares the Streams
//     screen's cache entry).
//   - Download link: <a href="/recording/${encodedPath}?download=1" download>.
//     nginx rewrites `^/recording/(.+)` to the PHP handler; access control
//     is path-token only, so a plain anchor (no bearer) is correct here.
//     `?download=1` flips Content-Disposition to attachment.
//
// Conventions:
//   - All HTTP through `apiClient`. `toFormDataPhp` for every POST so the
//     PHP layer reads `$_POST`. Schedule cancel/delete go on the query
//     string because apiClient.del doesn't forward a body.
//   - Destructive actions confirm through <ConfirmDialog>.

import { useMemo, useRef, useState, type ComponentProps, type Dispatch, type FormEvent, type SetStateAction } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query'
import { MoreHorizontal } from 'lucide-react'

import { InlineError } from '@/components/ui'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SectionCard } from '@/components/ui/section-card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ResponsiveRows,
  StackedRows,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  numericCell,
  numericHead,
} from '@/components/ui/table'
import {
  apiClient,
  ApiError,
  scopedApiPath,
  toFormDataPhp,
} from '@/lib/api-client'
import {
  formatBytesParts,
  formatDateTime,
  formatDuration,
  viewerZoneLabel,
} from '@/lib/format'
import { useViewAsStore } from '@/stores/view-as-store'
import type {
  RecordingEntry,
  RecordingMutationResponse,
  RecordingsResponse,
  RecordingSchedule,
  RecordingScheduleMutationResponse,
  RecordingSchedulesResponse,
  StreamDetailResponse,
} from '@/lib/types'
import { cn, toApiDateTime } from '@/lib/utils'

export const Route = createFileRoute('/admin/recordings')({
  component: AdminRecordingsPage,
})

const QK_RECORDINGS = ['admin', 'recordings'] as const
const QK_RECORDINGS_SCHEDULE = ['admin', 'recordings', 'schedule'] as const
// Shared with routes/admin/streams.tsx so the Owner column reuses its cache.
const QK_STREAM_DETAIL = (suffix: string) =>
  ['admin', 'streams', 'detail', suffix] as const

function describeError(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Unknown error.'
}

type PendingAction =
  | { kind: 'delete'; recording: RecordingEntry }
  | { kind: 'clear-featured' }
  | null

type PendingScheduleAction =
  | { kind: 'cancel' | 'delete'; schedule: RecordingSchedule }
  | null

type PlayMode = 'once' | 'repeat'
const REPEAT_COUNT = 3
const PLAY_MODE_LABEL: Record<PlayMode, string> = {
  once: 'Play once',
  repeat: `Play ${REPEAT_COUNT} times`,
}

// ── Module-scope cache helpers ─────────────────────────────────────────
function writeRecordingsBack(
  queryClient: QueryClient,
  viewAsId: number | null,
  data: RecordingMutationResponse,
): void {
  if (data.recordings !== undefined) {
    queryClient.setQueryData<RecordingsResponse>(
      [...QK_RECORDINGS, viewAsId],
      {
        success: true,
        recordings: data.recordings,
        featured: data.featured ?? null,
      },
    )
  } else {
    void queryClient.invalidateQueries({ queryKey: QK_RECORDINGS })
  }
}

function writeSchedulesBack(
  queryClient: QueryClient,
  viewAsId: number | null,
  data: RecordingScheduleMutationResponse,
): void {
  if (data.schedules !== undefined) {
    queryClient.setQueryData<RecordingSchedulesResponse>(
      [...QK_RECORDINGS_SCHEDULE, viewAsId],
      { success: true, schedules: data.schedules },
    )
  } else {
    void queryClient.invalidateQueries({ queryKey: QK_RECORDINGS_SCHEDULE })
  }
}

function recordingDownloadHref(path: string): string {
  return `/recording/${path.split('/').map(encodeURIComponent).join('/')}?download=1`
}

function AdminRecordingsPage() {
  const queryClient = useQueryClient()
  const viewAsId = useViewAsStore((s) => s.viewAsId)
  const vaSuffix: string = viewAsId == null ? '' : `view_as=${String(viewAsId)}`

  const [scheduleModalPath, setScheduleModalPath] = useState<string | null>(null)
  const [editingPath, setEditingPath] = useState<string | null>(null)
  const [labelDrafts, setLabelDrafts] = useState<Record<string, string>>({})
  const [pending, setPending] = useState<PendingAction>(null)
  const [pendingSchedule, setPendingSchedule] = useState<PendingScheduleAction>(null)
  const [inlineError, setInlineError] = useState<string | null>(null)
  // Focus returns to the control that opened a confirmation.
  const confirmTriggerRef = useRef<HTMLElement | null>(null)

  // ── Queries ─────────────────────────────────────────────────────
  const recordingsQuery = useQuery({
    queryKey: [...QK_RECORDINGS, viewAsId] as const,
    queryFn: () =>
      apiClient.get<RecordingsResponse>(scopedApiPath('/recordings/', viewAsId)),
  })

  const schedulesQuery = useQuery({
    queryKey: [...QK_RECORDINGS_SCHEDULE, viewAsId] as const,
    queryFn: () =>
      apiClient.get<RecordingSchedulesResponse>(
        scopedApiPath('/recordings/schedule/', viewAsId),
      ),
    select: (data: RecordingSchedulesResponse): RecordingSchedule[] => data.schedules,
  })

  // Stream keys, for the Owner column: token → name.
  const streamsQuery = useQuery({
    queryKey: QK_STREAM_DETAIL(vaSuffix),
    queryFn: () =>
      apiClient.get<StreamDetailResponse>(scopedApiPath('/stream/', viewAsId)),
  })
  const keyNames = useMemo(() => {
    const map = new Map<string, string>()
    for (const s of streamsQuery.data?.streams ?? []) map.set(s.token, s.name)
    return map
  }, [streamsQuery.data])

  // ── Mutations ───────────────────────────────────────────────────
  const labelRecording = useMutation({
    mutationFn: (input: { path: string; label: string }) =>
      apiClient.post<RecordingMutationResponse>(
        '/recordings/',
        toFormDataPhp({ action: 'label', path: input.path, label: input.label }),
      ),
    onSuccess: (data, vars) => {
      setInlineError(null)
      setLabelDrafts((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, vars.path)) return prev
        const clone = { ...prev }
        delete clone[vars.path]
        return clone
      })
      setEditingPath((cur) => (cur === vars.path ? null : cur))
      writeRecordingsBack(queryClient, viewAsId, data)
    },
    onError: (err) => setInlineError(describeError(err)),
  })

  const deleteRecording = useMutation({
    mutationFn: (path: string) =>
      apiClient.post<RecordingMutationResponse>(
        '/recordings/',
        toFormDataPhp({ action: 'delete', path }),
      ),
    onSuccess: (data) => {
      setInlineError(null)
      setPending(null)
      writeRecordingsBack(queryClient, viewAsId, data)
    },
    onError: (err) => setInlineError(describeError(err)),
  })

  const clearFeatured = useMutation({
    mutationFn: () =>
      apiClient.post<RecordingMutationResponse>(
        '/recordings/',
        toFormDataPhp({ action: 'clear' }),
      ),
    onSuccess: (data) => {
      setInlineError(null)
      setPending(null)
      writeRecordingsBack(queryClient, viewAsId, data)
    },
    onError: (err) => setInlineError(describeError(err)),
  })

  const createSchedule = useMutation({
    mutationFn: (input: {
      path: string
      startAt: string
      endAt: string
      durationMins: string
      useEndAt: boolean
      playMode: PlayMode
    }) => {
      const body: Record<string, unknown> = {
        action: 'create',
        path: input.path,
        start_at: toApiDateTime(input.startAt),
        play_mode: input.playMode,
      }
      if (input.useEndAt && input.endAt !== '') {
        body.end_at = toApiDateTime(input.endAt)
      } else if (!input.useEndAt && input.durationMins !== '') {
        body.duration_seconds = parseInt(input.durationMins, 10) * 60
      }
      return apiClient.post<RecordingScheduleMutationResponse>(
        '/recordings/schedule/',
        toFormDataPhp(body),
      )
    },
    onSuccess: (data) => {
      setInlineError(null)
      setScheduleModalPath(null)
      writeSchedulesBack(queryClient, viewAsId, data)
    },
    onError: (err) => setInlineError(describeError(err)),
  })

  const mutateSchedule = useMutation({
    mutationFn: (input: { action: 'cancel' | 'delete'; id: number }) =>
      apiClient.del<RecordingScheduleMutationResponse>(
        `/recordings/schedule/?action=${encodeURIComponent(input.action)}&id=${input.id}`,
      ),
    onSuccess: (data) => {
      setInlineError(null)
      setPendingSchedule(null)
      writeSchedulesBack(queryClient, viewAsId, data)
    },
    onError: (err) => setInlineError(describeError(err)),
  })

  // ── Derived view state ──────────────────────────────────────────
  const recordingsData = recordingsQuery.data?.recordings
  const recordings = useMemo((): RecordingEntry[] => recordingsData ?? [], [recordingsData])
  const featured: string | null = recordingsQuery.data?.featured ?? null
  const schedules: RecordingSchedule[] = schedulesQuery.data ?? []

  const featuredRecording = useMemo(
    (): RecordingEntry | null =>
      featured === null ? null : (recordings.find((r) => r.path === featured) ?? null),
    [recordings, featured],
  )
  const recordingName = (path: string): string => {
    const rec = recordings.find((r) => r.path === path)
    return rec?.label ?? rec?.filename ?? path
  }

  const openConfirm = (trigger: HTMLElement | null, action: PendingAction): void => {
    confirmTriggerRef.current = trigger
    setInlineError(null)
    setPending(action)
  }

  const confirmDestructive = (): void => {
    if (pending === null) return
    if (pending.kind === 'delete') deleteRecording.mutate(pending.recording.path)
    else clearFeatured.mutate()
  }

  const confirmDestructiveSchedule = (): void => {
    if (pendingSchedule === null) return
    mutateSchedule.mutate({ action: pendingSchedule.kind, id: pendingSchedule.schedule.id })
  }

  const rowProps = {
    featured,
    keyNames,
    editingPath,
    setEditingPath,
    labelDrafts,
    setLabelDrafts,
    labelRecording,
    deleteRecording,
    onSchedule: setScheduleModalPath,
    onDelete: (r: RecordingEntry, trigger: HTMLElement | null) =>
      openConfirm(trigger, { kind: 'delete', recording: r }),
  }

  const confirmBusy = deleteRecording.isPending || clearFeatured.isPending

  return (
    <div data-testid="admin-recordings" className="space-y-4">
      <SectionCard
        title="Recordings"
        description="Saved captures from your stream keys. Download, rename, schedule a front-page play, or delete."
      >
        <div className="space-y-4">
          {inlineError !== null && pending === null && pendingSchedule === null && (
            <InlineError message={inlineError} data-testid="admin-recordings-error" />
          )}

          {featuredRecording !== null && (
            <div
              data-testid="admin-recordings-featured"
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-cyber-cyan/40 bg-cyber-cyan/5 px-3 py-2"
            >
              <div className="min-w-0">
                <p className="text-xs font-medium text-cyber-cyan">Playing on front page</p>
                <p className="truncate text-sm text-foreground" title={featuredRecording.path}>
                  {featuredRecording.label ?? featuredRecording.filename}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="admin-recordings-clear"
                disabled={clearFeatured.isPending}
                onClick={(e) => openConfirm(e.currentTarget, { kind: 'clear-featured' })}
              >
                Stop playing
              </Button>
            </div>
          )}

          {recordingsQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : recordingsQuery.isError ? (
            <InlineError message="Couldn't load recordings. Try refreshing." />
          ) : recordings.length === 0 ? (
            <div
              data-testid="admin-recordings-empty"
              className="rounded-md border border-dashed border-border/60 px-4 py-6 text-center text-sm text-muted-foreground"
            >
              No recordings yet. A recording appears here after a stream ends with Recording turned on.
            </div>
          ) : (
            <ResponsiveRows
              table={
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-full">Recording</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead className={numericHead}>Recorded</TableHead>
                      <TableHead className={numericHead}>Size</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recordings.map((rec) => (
                      <RecordingRow key={rec.path} rec={rec} {...rowProps} />
                    ))}
                  </TableBody>
                </Table>
              }
              stacked={
                <StackedRows
                  rows={recordings}
                  rowKey={(r) => r.path}
                  rowProps={(r) => ({
                    'data-testid': 'admin-recordings-row',
                    'data-recording-path': r.path,
                  })}
                  primary={(r) => (
                    <RecordingIdentity rec={r} isFeatured={featured === r.path} {...rowProps} />
                  )}
                  secondary={(r) => <SizeValue bytes={r.size} />}
                  fields={[
                    {
                      label: 'Recorded',
                      value: (r) => formatDateTime(r.modified, { withZone: true }),
                      numeric: true,
                    },
                    { label: 'Owner', value: (r) => <OwnerValue rec={r} keyNames={keyNames} /> },
                  ]}
                  actions={(r) => <RecordingActions rec={r} {...rowProps} />}
                />
              }
            />
          )}
        </div>
      </SectionCard>

      <SectionCard
        title="Scheduled plays"
        description="Recordings queued to play on the front page at a set time."
      >
        <div className="space-y-3">
          {schedulesQuery.isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : schedulesQuery.isError ? (
            <InlineError message="Couldn't load schedules. Try refreshing." />
          ) : schedules.length === 0 ? (
            <div
              data-testid="admin-recordings-schedule-empty"
              className="rounded-md border border-dashed border-border/60 px-4 py-6 text-center text-sm text-muted-foreground"
            >
              Nothing scheduled. Choose Schedule on a recording to play it on the front page at a set time.
            </div>
          ) : (
            <ResponsiveRows
              table={
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-full">Recording</TableHead>
                      <TableHead className={numericHead}>Starts</TableHead>
                      <TableHead className={numericHead}>Ends / length</TableHead>
                      <TableHead>Plays</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {schedules.map((s) => (
                      <ScheduleRow
                        key={s.id}
                        s={s}
                        name={recordingName(s.recording_path)}
                        mutateSchedule={mutateSchedule}
                        onCancel={(s, trigger) => {
                          confirmTriggerRef.current = trigger
                          setPendingSchedule({ kind: 'cancel', schedule: s })
                        }}
                        onDelete={(s, trigger) => {
                          confirmTriggerRef.current = trigger
                          setPendingSchedule({ kind: 'delete', schedule: s })
                        }}
                      />
                    ))}
                  </TableBody>
                </Table>
              }
              stacked={
                <StackedRows
                  rows={schedules}
                  rowKey={(s) => s.id}
                  rowProps={(s) => ({
                    'data-testid': 'admin-recordings-schedule-row',
                    'data-schedule-id': s.id,
                  })}
                  primary={(s) => (
                    <span className="block truncate" title={s.recording_path}>
                      {recordingName(s.recording_path)}
                    </span>
                  )}
                  secondary={(s) => <ScheduleStatusPill status={s.status} />}
                  fields={[
                    { label: 'Starts', value: (s) => formatDateTime(s.start_at, { withZone: true }), numeric: true },
                    { label: 'Ends', value: scheduleEndLabel, numeric: true },
                    { label: 'Plays', value: (s) => (s.play_mode === 'repeat' ? `${REPEAT_COUNT} times` : 'Once') },
                  ]}
                  actions={(s) => (
                    <ScheduleActions
                      s={s}
                      mutateSchedule={mutateSchedule}
                      onCancel={(s, trigger) => {
                        confirmTriggerRef.current = trigger
                        setPendingSchedule({ kind: 'cancel', schedule: s })
                      }}
                      onDelete={(s, trigger) => {
                        confirmTriggerRef.current = trigger
                        setPendingSchedule({ kind: 'delete', schedule: s })
                      }}
                    />
                  )}
                />
              }
            />
          )}
        </div>
      </SectionCard>

      <ScheduleDialog
        path={scheduleModalPath}
        name={scheduleModalPath !== null ? recordingName(scheduleModalPath) : ''}
        pending={createSchedule.isPending}
        error={createSchedule.error !== null ? describeError(createSchedule.error) : null}
        onClose={() => {
          setScheduleModalPath(null)
          createSchedule.reset()
        }}
        onSubmit={(payload) => {
          if (scheduleModalPath === null) return
          createSchedule.mutate({ path: scheduleModalPath, ...payload })
        }}
      />

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null)
        }}
        title={
          pending?.kind === 'delete'
            ? `Delete “${pending.recording.label ?? pending.recording.filename}”?`
            : 'Stop playing on the front page?'
        }
        description={
          pending?.kind === 'delete'
            ? pending.recording.path === featured
              ? 'This recording is playing on the front page right now. Deleting it stops that playback immediately and removes the file permanently. This can’t be undone.'
              : 'The file is removed permanently. This can’t be undone.'
            : 'The front page returns to the live player. The recording itself is kept.'
        }
        confirmLabel={pending?.kind === 'delete' ? 'Delete recording' : 'Stop playing'}
        busyLabel={pending?.kind === 'delete' ? 'Deleting…' : 'Stopping…'}
        confirmVariant={pending?.kind === 'delete' ? 'destructive-solid' : 'default'}
        busy={confirmBusy}
        error={pending ? inlineError : null}
        returnFocusTo={confirmTriggerRef}
        onConfirm={confirmDestructive}
        data-testid="admin-recordings-confirm"
      />

      <ConfirmDialog
        open={pendingSchedule !== null}
        onOpenChange={(open) => {
          if (!open) setPendingSchedule(null)
        }}
        title={pendingSchedule?.kind === 'delete' ? 'Delete scheduled play?' : 'Cancel scheduled play?'}
        description={
          pendingSchedule
            ? pendingSchedule.kind === 'delete'
              ? `The schedule for “${recordingName(pendingSchedule.schedule.recording_path)}” is removed. The recording itself is kept.`
              : `“${recordingName(pendingSchedule.schedule.recording_path)}” will not play at ${formatDateTime(pendingSchedule.schedule.start_at)}. The schedule stays listed as canceled.`
            : ''
        }
        confirmLabel={pendingSchedule?.kind === 'delete' ? 'Delete schedule' : 'Cancel play'}
        busyLabel={pendingSchedule?.kind === 'delete' ? 'Deleting…' : 'Canceling…'}
        cancelLabel="Keep it"
        confirmVariant="destructive-solid"
        busy={mutateSchedule.isPending}
        error={pendingSchedule ? inlineError : null}
        returnFocusTo={confirmTriggerRef}
        onConfirm={confirmDestructiveSchedule}
        data-testid="admin-recordings-schedule-confirm"
      />
    </div>
  )
}

// ── Schedule dialog ────────────────────────────────────────────────

interface ScheduleDialogProps {
  path: string | null
  name: string
  pending: boolean
  error: string | null
  onClose: () => void
  onSubmit: (payload: {
    startAt: string
    endAt: string
    durationMins: string
    useEndAt: boolean
    playMode: PlayMode
  }) => void
}

function ScheduleDialog({ path, name, pending, error, onClose, onSubmit }: ScheduleDialogProps) {
  const [startAt, setStartAt] = useState<string>('')
  const [endAt, setEndAt] = useState<string>('')
  const [durationMins, setDurationMins] = useState<string>('')
  const [useEndAt, setUseEndAt] = useState<boolean>(true)
  const [playMode, setPlayMode] = useState<PlayMode>('once')
  const zone = viewerZoneLabel()

  // datetime-local values compare lexically when both are set.
  const endBeforeStart = useEndAt && startAt !== '' && endAt !== '' && endAt <= startAt
  const canSave = startAt !== '' && !pending && !endBeforeStart

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    if (!canSave) return
    onSubmit({ startAt, endAt, durationMins, useEndAt, playMode })
  }

  const reset = (): void => {
    setStartAt('')
    setEndAt('')
    setDurationMins('')
    setUseEndAt(true)
    setPlayMode('once')
  }

  return (
    <Dialog
      open={path !== null}
      onOpenChange={(open) => {
        if (!open) {
          reset()
          onClose()
        }
      }}
    >
      <DialogContent data-testid="admin-recordings-schedule-modal">
        <form className="space-y-4" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Schedule a front-page play</DialogTitle>
            <DialogDescription>
              {name !== '' ? (
                <>
                  <span className="text-foreground">{name}</span>
                  {path !== null && path !== name ? (
                    <span className="ml-2 font-mono text-xs" title={path}>{path}</span>
                  ) : null}
                </>
              ) : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="admin-recordings-schedule-start">Start</Label>
              <Input
                id="admin-recordings-schedule-start"
                data-testid="admin-recordings-schedule-start"
                type="datetime-local"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
                required
                disabled={pending}
                aria-describedby="admin-recordings-schedule-zone"
              />
              <p id="admin-recordings-schedule-zone" className="text-xs text-muted-foreground">
                Times are in your local time zone ({zone}).
              </p>
            </div>

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Ends</legend>
              <div className="flex flex-wrap gap-2" role="group" aria-label="How the play ends">
                <ChoiceButton
                  pressed={useEndAt}
                  data-testid="admin-recordings-schedule-mode-end"
                  onClick={() => setUseEndAt(true)}
                >
                  At a date and time
                </ChoiceButton>
                <ChoiceButton
                  pressed={!useEndAt}
                  data-testid="admin-recordings-schedule-mode-duration"
                  onClick={() => setUseEndAt(false)}
                >
                  After a number of minutes
                </ChoiceButton>
              </div>
              {useEndAt ? (
                <div className="space-y-1.5">
                  <Label htmlFor="admin-recordings-schedule-end">End ({zone})</Label>
                  <Input
                    id="admin-recordings-schedule-end"
                    data-testid="admin-recordings-schedule-end"
                    type="datetime-local"
                    value={endAt}
                    min={startAt || undefined}
                    onChange={(e) => setEndAt(e.target.value)}
                    disabled={pending}
                    aria-invalid={endBeforeStart || undefined}
                    aria-describedby={endBeforeStart ? 'admin-recordings-schedule-end-error' : undefined}
                  />
                  {endBeforeStart ? (
                    <p id="admin-recordings-schedule-end-error" role="alert" className="text-xs text-destructive">
                      End must be after start.
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Leave empty to play the whole recording.</p>
                  )}
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="admin-recordings-schedule-duration">Length (minutes)</Label>
                  <Input
                    id="admin-recordings-schedule-duration"
                    data-testid="admin-recordings-schedule-duration"
                    type="number"
                    min={1}
                    step={1}
                    value={durationMins}
                    onChange={(e) => setDurationMins(e.target.value)}
                    placeholder="e.g. 30"
                    disabled={pending}
                    className="font-mono tabular-nums"
                  />
                </div>
              )}
            </fieldset>

            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Plays</legend>
              <div className="flex flex-wrap gap-2" role="group" aria-label="How many times to play">
                <ChoiceButton
                  pressed={playMode === 'once'}
                  data-testid="admin-recordings-schedule-playmode-once"
                  onClick={() => setPlayMode('once')}
                >
                  {PLAY_MODE_LABEL.once}
                </ChoiceButton>
                <ChoiceButton
                  pressed={playMode === 'repeat'}
                  data-testid="admin-recordings-schedule-playmode-repeat"
                  onClick={() => setPlayMode('repeat')}
                >
                  {PLAY_MODE_LABEL.repeat}
                </ChoiceButton>
              </div>
            </fieldset>

            {error !== null && <InlineError message={error} />}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                reset()
                onClose()
              }}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="admin-recordings-schedule-save"
              disabled={!canSave}
            >
              {pending ? 'Scheduling…' : 'Schedule play'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Outline toggle used for the two-way choices in the schedule form. */
function ChoiceButton({
  pressed,
  className,
  ...props
}: ComponentProps<typeof Button> & { pressed: boolean }) {
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      aria-pressed={pressed}
      className={cn(pressed && 'border-cyber-cyan bg-cyber-cyan/10 text-cyber-cyan', className)}
      {...props}
    />
  )
}

// ── Recording rows ─────────────────────────────────────────────────

interface RecordingRowProps {
  rec: RecordingEntry
  featured: string | null
  keyNames: Map<string, string>
  editingPath: string | null
  setEditingPath: Dispatch<SetStateAction<string | null>>
  labelDrafts: Record<string, string>
  setLabelDrafts: Dispatch<SetStateAction<Record<string, string>>>
  labelRecording: UseMutationResult<
    RecordingMutationResponse,
    Error,
    { path: string; label: string }
  >
  deleteRecording: UseMutationResult<RecordingMutationResponse, Error, string>
  onSchedule: (path: string) => void
  onDelete: (rec: RecordingEntry, trigger: HTMLElement | null) => void
}

function SizeValue({ bytes }: { bytes: number }) {
  const { value, unit } = formatBytesParts(bytes)
  return (
    <span className="whitespace-nowrap font-mono tabular-nums">
      {value}
      {unit ? <span className="ml-1 text-xs text-muted-foreground">{unit}</span> : null}
    </span>
  )
}

function OwnerValue({ rec, keyNames }: { rec: RecordingEntry; keyNames: Map<string, string> }) {
  if (rec.key === null) return <span className="text-muted-foreground">Shared</span>
  const name = keyNames.get(rec.key)
  if (name) return <span className="whitespace-nowrap">{name}</span>
  return <span className="font-mono text-xs text-muted-foreground" title="Stream key no longer exists">{rec.key}</span>
}

/** Name (or filename) with the inline rename editor and the “Playing” mark. */
function RecordingIdentity({
  rec,
  isFeatured,
  editingPath,
  setEditingPath,
  labelDrafts,
  setLabelDrafts,
  labelRecording,
}: Pick<RecordingRowProps, 'rec' | 'editingPath' | 'setEditingPath' | 'labelDrafts' | 'setLabelDrafts' | 'labelRecording'> & { isFeatured: boolean }) {
  const isEditing = editingPath === rec.path
  const hasDraft = Object.prototype.hasOwnProperty.call(labelDrafts, rec.path)
  const baseLabel: string = rec.label ?? ''
  const draftValue: string = hasDraft ? labelDrafts[rec.path] : baseLabel
  const dirty = hasDraft && draftValue !== baseLabel
  const busyLabel = labelRecording.isPending && labelRecording.variables?.path === rec.path

  const cancelEdit = (): void => {
    setLabelDrafts((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, rec.path)) return prev
      const clone = { ...prev }
      delete clone[rec.path]
      return clone
    })
    setEditingPath(null)
  }

  if (isEditing) {
    return (
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          if (!dirty || busyLabel) return
          labelRecording.mutate({ path: rec.path, label: draftValue.trim() })
        }}
      >
        <Input
          type="text"
          autoFocus
          aria-label="Recording name"
          value={draftValue}
          placeholder={rec.filename}
          disabled={busyLabel}
          data-testid="admin-recordings-label-input"
          className="h-8 min-w-[12rem] flex-1"
          onChange={(e) =>
            setLabelDrafts((prev) => ({ ...prev, [rec.path]: e.target.value }))
          }
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              cancelEdit()
            }
          }}
        />
        <Button
          type="submit"
          size="sm"
          className="h-8"
          data-testid="admin-recordings-label-save"
          disabled={!dirty || busyLabel}
        >
          {busyLabel ? 'Saving…' : 'Save'}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8"
          data-testid="admin-recordings-label-cancel"
          onClick={cancelEdit}
        >
          Cancel
        </Button>
      </form>
    )
  }

  return (
    <div className="min-w-0">
      <div className="flex min-w-0 items-center gap-2">
        <span className="truncate font-medium text-foreground" title={rec.path}>
          {rec.label ?? rec.filename}
        </span>
        {isFeatured ? (
          <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full border border-cyber-cyan/40 bg-cyber-cyan/10 px-2 py-0.5 text-xs font-medium text-cyber-cyan">
            Playing
          </span>
        ) : null}
      </div>
      {rec.label !== null ? (
        <div className="truncate font-mono text-xs text-muted-foreground" title={rec.path}>
          {rec.filename}
        </div>
      ) : null}
    </div>
  )
}

function RecordingActions({
  rec,
  editingPath,
  setEditingPath,
  labelRecording,
  deleteRecording,
  onSchedule,
  onDelete,
}: Omit<RecordingRowProps, 'featured' | 'keyNames' | 'labelDrafts' | 'setLabelDrafts'>) {
  const isEditing = editingPath === rec.path
  const busy =
    (labelRecording.isPending && labelRecording.variables?.path === rec.path) ||
    (deleteRecording.isPending && deleteRecording.variables === rec.path)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  return (
    <>
      <Button
        asChild
        type="button"
        variant="outline"
        size="sm"
        data-testid="admin-recordings-download"
      >
        <a
          href={recordingDownloadHref(rec.path)}
          download={rec.label ? `${rec.label.replace(/[\\/]+/g, '_')}.flv` : rec.filename}
          rel="noopener"
        >
          Download
        </a>
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            ref={triggerRef}
            type="button"
            variant="outline"
            size="sm"
            className="px-2"
            disabled={busy}
            aria-label={`More actions for ${rec.label ?? rec.filename}`}
          >
            <MoreHorizontal aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {rec.transcript_portal_path !== null ? (
            <DropdownMenuItem asChild data-testid="admin-recordings-transcript">
              <a href={rec.transcript_portal_path} target="_blank" rel="noopener noreferrer">
                Open transcript
              </a>
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem
            data-testid="admin-recordings-label"
            onSelect={() => setEditingPath((cur) => (cur === rec.path ? null : rec.path))}
          >
            {isEditing ? 'Close rename' : 'Rename'}
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="admin-recordings-schedule"
            onSelect={() => onSchedule(rec.path)}
          >
            Schedule a play
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="admin-recordings-delete"
            className="text-destructive focus:text-destructive"
            onSelect={() => onDelete(rec, triggerRef.current)}
          >
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

function RecordingRow(props: RecordingRowProps) {
  const { rec, featured, keyNames, labelRecording, deleteRecording } = props
  const busy =
    (labelRecording.isPending && labelRecording.variables?.path === rec.path) ||
    (deleteRecording.isPending && deleteRecording.variables === rec.path)

  return (
    <TableRow
      data-testid="admin-recordings-row"
      data-recording-path={rec.path}
      className={busy ? 'opacity-60' : undefined}
    >
      <TableCell className="min-w-[16rem] max-w-[28rem]">
        <RecordingIdentity isFeatured={featured === rec.path} {...props} />
      </TableCell>
      <TableCell className="whitespace-nowrap text-sm">
        <OwnerValue rec={rec} keyNames={keyNames} />
      </TableCell>
      <TableCell className={cn(numericCell, 'text-sm text-muted-foreground')}>
        {formatDateTime(rec.modified, { withZone: true })}
      </TableCell>
      <TableCell className={cn(numericCell, 'text-sm')}>
        <SizeValue bytes={rec.size} />
      </TableCell>
      <TableCell className="w-px whitespace-nowrap">
        <div className="flex items-center justify-end gap-2">
          <RecordingActions {...props} />
        </div>
      </TableCell>
    </TableRow>
  )
}

// ── Schedule rows ──────────────────────────────────────────────────

function scheduleEndLabel(s: RecordingSchedule): string {
  if (s.end_at !== null) return formatDateTime(s.end_at, { withZone: true })
  if (s.duration_seconds !== null) return formatDuration(s.duration_seconds)
  return 'Whole recording'
}

const SCHEDULE_STATUS_LABEL: Record<RecordingSchedule['status'], string> = {
  pending: 'Pending',
  active: 'Playing',
  done: 'Done',
  canceled: 'Canceled',
}

function ScheduleStatusPill({ status }: { status: RecordingSchedule['status'] }) {
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium',
        status === 'active'
          ? 'border-cyber-cyan/40 bg-cyber-cyan/10 text-cyber-cyan'
          : 'border-border/60 bg-muted/40 text-muted-foreground',
      )}
    >
      {SCHEDULE_STATUS_LABEL[status] ?? status}
    </span>
  )
}

interface ScheduleActionProps {
  s: RecordingSchedule
  mutateSchedule: UseMutationResult<
    RecordingScheduleMutationResponse,
    Error,
    { action: 'cancel' | 'delete'; id: number }
  >
  onCancel: (s: RecordingSchedule, trigger: HTMLElement | null) => void
  onDelete: (s: RecordingSchedule, trigger: HTMLElement | null) => void
}

function ScheduleActions({ s, mutateSchedule, onCancel, onDelete }: ScheduleActionProps) {
  const busy = mutateSchedule.isPending && mutateSchedule.variables?.id === s.id
  const cancellable = s.status === 'pending' || s.status === 'active'
  return (
    <>
      {cancellable ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="admin-recordings-schedule-cancel"
          disabled={busy}
          onClick={(e) => onCancel(s, e.currentTarget)}
        >
          Cancel play
        </Button>
      ) : null}
      <Button
        type="button"
        variant="destructive"
        size="sm"
        data-testid="admin-recordings-schedule-delete"
        disabled={busy}
        onClick={(e) => onDelete(s, e.currentTarget)}
      >
        Delete
      </Button>
    </>
  )
}

function ScheduleRow({ s, name, ...actions }: ScheduleActionProps & { name: string }) {
  const busy = actions.mutateSchedule.isPending && actions.mutateSchedule.variables?.id === s.id
  return (
    <TableRow
      data-testid="admin-recordings-schedule-row"
      data-schedule-id={s.id}
      className={busy ? 'opacity-60' : undefined}
    >
      <TableCell className="min-w-[12rem] max-w-[24rem]">
        <div className="truncate font-medium text-foreground" title={s.recording_path}>{name}</div>
        {name !== s.recording_path ? (
          <div className="truncate font-mono text-xs text-muted-foreground">{s.recording_path}</div>
        ) : null}
      </TableCell>
      <TableCell className={cn(numericCell, 'text-sm text-muted-foreground')}>
        {formatDateTime(s.start_at, { withZone: true })}
      </TableCell>
      <TableCell className={cn(numericCell, 'text-sm text-muted-foreground')}>
        {scheduleEndLabel(s)}
      </TableCell>
      <TableCell className="whitespace-nowrap text-sm">
        {s.play_mode === 'repeat' ? `${REPEAT_COUNT} times` : 'Once'}
      </TableCell>
      <TableCell>
        <ScheduleStatusPill status={s.status} />
      </TableCell>
      <TableCell className="w-px whitespace-nowrap">
        <div className="flex items-center justify-end gap-2">
          <ScheduleActions s={s} {...actions} />
        </div>
      </TableCell>
    </TableRow>
  )
}
